import {
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
  Flex,
  FlexProps,
  IconButton,
  InputContainer,
  Menu,
  Text,
  ToggleButton,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { useContext } from 'react'
import { useOnline, useWorkspaces } from 'utils'
import { useFacilities } from 'utils/facilityUtils'
import { useTemplates } from 'utils/templateUtils'
import { useSource } from '../../utils/preferences'
import { templateOverride } from '../../utils/templateOverride'
import FileLoader from '../FileLoader'
import { LoadingContext, useExplorerConfig } from '../providers'

export default function ContentSelect(props: FlexProps) {
  const online = useOnline()
  const { copyLatestTemplate } = useContext(LoadingContext)
  const { workspace, updateWorkspace } = useExplorerConfig()
  const [source, setSource] = useSource()
  const { workspaces } = useWorkspaces('emp')
  const user = useCurrentUser()

  if (templateOverride) return <Text>Override Template: {templateOverride.title}</Text>

  return (
    <Flex direction="column" gap={6} {...props}>
      {!online || workspaces?.length === 1 ? (
        <OfflineInput label="Workspace" text={workspace?.title || 'No Workspaces Available'} />
      ) : (
        <Menu
          borderRadius="sm"
          colorMode="dark"
          disabled={!workspaces}
          label="Workspace"
          menuProps={{ positioning: { sameWidth: true } }}
          onChange={updateWorkspace}
          options={workspaces?.map(({ id, title }) => ({ value: id, label: title || 'Draft' }))}
          placeholder="Select Workspace"
          portal
          value={workspace?.id}
          variant="outline"
        />
      )}

      {user?.isAdmin && (
        <ToggleButton
          label="Source"
          alignSelf="flex-start"
          options={['facility', 'standalone']}
          value={source}
          onChange={setSource}
        />
      )}

      {user?.isAdmin && source === 'standalone' ? (
        <Flex alignItems="flex-end" gap={1}>
          <TemplateSelect />
          <IconButton
            aria-label="Copy Template"
            disabled={!copyLatestTemplate}
            icon={<ClipboardIcon type="outline" />}
            onClick={copyLatestTemplate}
          />
        </Flex>
      ) : (
        <FacilitySelect />
      )}

      <FileLoader flex={1} />
    </Flex>
  )
}

function FacilitySelect() {
  const { facility, updateFacility, workspace } = useExplorerConfig()
  const { facilities, refetch } = useFacilities(workspace?.id)
  const online = useOnline()

  if (!online && facility) return <OfflineInput label="Facility" text={facility.title} />

  return (
    <Flex gap={2} alignItems="flex-end">
      <Menu
        borderRadius="sm"
        colorMode="dark"
        disabled={!facilities?.length}
        label="Facility"
        onChange={updateFacility}
        options={facilities?.map(({ id, title }) => ({ value: id, label: title || 'Draft' }))}
        placeholder={facilities?.length === 0 ? 'No Facilities Available' : 'Select Facility'}
        portal
        value={facility?.id}
        variant="outline"
      />

      <IconButton
        aria-label="Refresh"
        disabled={status !== 'done' || !facilities}
        icon={<ArrowPathRoundedSquareIcon />}
        onClick={() => refetch()}
        size="md"
        variant="solid"
      />
    </Flex>
  )
}

function TemplateSelect() {
  const { updateTemplateId, workspace, baseTemplate, templateDocs } = useExplorerConfig()
  const { templates } = useTemplates(workspace?.id)
  const online = useOnline()

  if (!online && templateDocs)
    return <OfflineInput label="Template" text={templateDocs.template.title} />

  return (
    <Menu
      borderRadius="sm"
      colorMode="dark"
      disabled={!templates}
      label="Template"
      onChange={updateTemplateId}
      options={templates?.map(({ id, title }) => ({ value: id, label: title }))}
      placeholder="Select Template"
      portal
      value={baseTemplate?.id ?? ''}
      variant="outline"
    />
  )
}

function OfflineInput({ label, text }: { label: string; text: string }) {
  return (
    <InputContainer label={label}>
      <Text textStyle="bodySmall" border="1px solid white" py={3} px={5} borderRadius="sm">
        {text}
      </Text>
    </InputContainer>
  )
}
